/**
 * Defines inputs for the ReportWidget component, used to display scores in the
 * PatientChart, MyHealthNumbers, and MyLifestyle components.
 */

import {store} from '@/apps/default/store'
import Colors from '@/providers/GaugeColors'

export default {
    data () {
        return {
            interval: 3,
        }
    },
    methods: {
        getMeterLabel(meter) {
            if (meter.scoreName.slice(0, 4) == 'WBI_') {
                // display score as part of label
                const scoreDef = this.$store.state.patientParticipant.latestScoreDefs[meter.scoreName]
                return this.$i18n.gt(meter.labelKey) + ' (' + Math.floor(scoreDef.score.score) + '/10)'
            } else if (meter.labelKey) {
                return this.$i18n.gt(meter.labelKey)
            } else if (meter.biometric && meter.biometric.labelKey) {
                return this.$i18n.gt(meter.biometric.labelKey)
            } else {
                return ''
            }
        },
        // used in PlanResources.vue
        getWidgetGroups () {
            return [
                {
                    heading: 'Lifestyle Risk Factors',
                    resourceKeys: ['SCORE_EXER', 'SCORE_DIET', 'SCORE_STRESS', 'SCORE_SLEEP']
                },
                {
                    heading: 'Biometric Risk Factors',
                    resourceKeys: ['BIO_BMI', 'SCORE_BP', 'SCORE_METAB', 'BIO_LDL',
                        'TOTAL_SCORE', 'FRAMINGHAM_SCORE', 'ACC_AHA_SCORE', 'SCORE_PMH',
                        'OK_PAIN', 'OK_PHYSICAL_FUNCTION', 'OK_GAIT_SPEED', 'OK_ADLS', 'OK_ASSESS_FALLS',
                        'SCORE_GAIT_SPEED', 'SCORE_SIT_TO_STAND', 'SCORE_STAIRS', 'SCORE_SIT_REACH', 'SCORE_CURLS']
                },
                {
                    heading: 'Social Factors',
                    resourceKeys: ['OK_LITERACY', 'OK_HEALTH_LITERACY', 'OK_SECURITY', 'OK_FOOD_SECURITY',
                        'OK_COMMUNITY', 'OK_TRANSPORTATION', 'OK_FINANCES', 'OK_POVERTY',
                        'OK_FEELS_WELL', 'OK_HRQOL', 'OK_SOCIAL_SUPPORT', 'OK_CENTERED',
                        'PHQ9_SCORE', 'GAD7_SCORE', 'AUDIT_SCORE']
                }
            ]
        },

        getWidgets () {
            return {
                // Lifestyle Risk Factors

                SCORE_EXER: {
                    headingKey: 'HEADING_REPORT_MOVEMENT',
                    meters: [
                        {
                            scoreName: 'SCORE_EXER',
                            labelKey: 'HEADING_REPORT_ACTIVITY'
                        },
                        {
                            scoreName: 'SCORE_SED',
                            labelKey: 'HEADING_REPORT_INACTIVITY',
                        },
                    ],
                    summaryPlanScore: { scoreName: 'SCORE_EXER', }
                },
                SCORE_DIET: {
                    headingKey: 'HEADING_REPORT_HEALTHY_EATING',
                    meters: [
                        {
                            scoreName: 'SCORE_DIET',
                            labelKey: 'HEADING_REPORT_DIET',
                        },
                        {
                            scoreName: 'SCORE_DIETID',
                            labelKey: 'HEADING_REPORT_DIETID',
                        },
                    ],
                    summaryPlanScore: { scoreName: 'SCORE_DIET' },
                },
                SCORE_STRESS: {
                    headingKey: 'HEADING_REPORT_EMOTIONAL_WELLBEING',
                    meters: [{
                        scoreName: 'SCORE_STRESS',
                        planScore: { scoreName: 'SCORE_STRESS' }
                    }],
                },
                SCORE_SLEEP: {
                    headingKey: 'HEADING_REPORT_SLEEP',
                    meters: [{
                        scoreName: 'SCORE_SLEEP',
                        planScore: { scoreName: 'SCORE_SLEEP' }
                    }],
                },
                QUICKIE_SCORE: {
                    headingKey: 'HEADING_REPORT_QUICKIE',
                    meters: [{
                        scoreName: 'QUICKIE_SCORE',
                        planScore: { scoreName: 'QUICKIE_SCORE' }
                    }],
                },
                RATIO_SCORE: {
                    headingKey: 'HEADING_REPORT_RATIO',
                },
                WBI_SCORE: {
                    headingKey: 'HEADING_WBI_SCORE',
                    customGaugeColors: Colors.CUSTOM_GAUGE_COLORS[store.state.theme.name],
                    meters: [
                        {
                            scoreName: 'WBI_MIND',
                            labelKey: 'HEADING_WBI_MIND',
                        },
                        {
                            scoreName: 'WBI_BODY',
                            labelKey: 'HEADING_WBI_BODY',
                        },
                        {
                            scoreName: 'WBI_WORK',
                            labelKey: 'HEADING_WBI_WORK',
                        },
                        {
                            scoreName: 'WBI_LIFE',
                            labelKey: 'HEADING_WBI_LIFE',
                        },
                        {
                            scoreName: 'WBI_OVERALL',
                            labelKey: 'HEADING_WBI_OVERALL',
                        },
                    ],
                    summaryPlanScore: { scoreName: 'WBI_SCORE', }
                },

                // RTC

                RTC_WELLBEING_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_WELLBEING',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_WELLBEING_IC',
                        },
                        {
                            scoreName: 'RTC_WELLBEING_RTC',
                        },
                    ],
                },

                RTC_ENERGY_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_ENERGY',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_ENERGY_IC',
                        },
                        {
                            scoreName: 'RTC_ENERGY_RTC',
                        },
                    ],
                },

                RTC_STRESS_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_STRESS',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_STRESS_IC',
                        },
                        {
                            scoreName: 'RTC_STRESS_RTC',
                        },
                    ],
                },

                RTC_PRODUCTIVITY_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_PRODUCTIVITY',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_PRODUCTIVITY_IC',
                        },
                        {
                            scoreName: 'RTC_PRODUCTIVITY_RTC',
                        },
                    ],
                },

                RTC_SATISFACTION_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_SATISFACTION',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_SATISFACTION_IC',
                        },
                        {
                            scoreName: 'RTC_SATISFACTION_RTC',
                        },
                    ],
                },

                RTC_WORK_LIFE_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_WORK_LIFE',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_WORK_LIFE_IC',
                        },
                        {
                            scoreName: 'RTC_WORK_LIFE_RTC',
                        },
                    ],
                },

                RTC_PHYS_ACTIVITY_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_PHYS_ACTIVITY',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_PHYS_ACTIVITY_IC',
                        },
                        {
                            scoreName: 'RTC_PHYS_ACTIVITY_RTC',
                        },
                    ],
                },

                RTC_EATING_HABITS_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_EATING_HABITS',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_EATING_HABITS_IC',
                        },
                        {
                            scoreName: 'RTC_EATING_HABITS_RTC',
                        },
                    ],
                },

                RTC_WEIGHT_CONTROL_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_WEIGHT_CONTROL',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_WEIGHT_CONTROL_IC',
                        },
                        {
                            scoreName: 'RTC_WEIGHT_CONTROL_RTC',
                        },
                    ],
                },

                RTC_SLEEP_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_SLEEP',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_SLEEP_IC',
                        },
                        {
                            scoreName: 'RTC_SLEEP_RTC',
                        },
                    ],
                },

                RTC_MANAGE_MEDICAL_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_MANAGE_MEDICAL',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_MANAGE_MEDICAL_IC',
                        },
                        {
                            scoreName: 'RTC_MANAGE_MEDICAL_RTC',
                        },
                    ],
                },

                RTC_REDUCE_NEED_MEDS_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_REDUCE_NEED_MEDS',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_REDUCE_NEED_MEDS_IC',
                        },
                        {
                            scoreName: 'RTC_REDUCE_NEED_MEDS_RTC',
                        },
                    ],
                },

                RTC_NICOTINE_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_NICOTINE',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_NICOTINE_IC',
                        },
                        {
                            scoreName: 'RTC_NICOTINE_RTC',
                        },
                    ],
                },

                RTC_ALCOHOL_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_ALCOHOL',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_ALCOHOL_IC',
                        },
                        {
                            scoreName: 'RTC_ALCOHOL_RTC',
                        },
                    ],
                },

                RTC_DRUG_USE_SCORE: {
                    headingKey: 'HEADING_REPORT_RTC_DRUG_USE',
                    rtcMeters: [
                        {
                            scoreName: 'RTC_DRUG_USE_IC',
                        },
                        {
                            scoreName: 'RTC_DRUG_USE_RTC',
                        },
                    ],
                },

                // Biometric Risk Factors

                BIO_BMI: {
                    headingKey: 'HEADING_REPORT_BMI_WAIST',
                    meters: [
                        {
                            scoreName: 'BIO_BMI',
                            biometric: { labelKey: 'HEADING_REPORT_BMI', units: '' },
                            planScore: { scoreName: 'BIO_BMI', }
                        },
                        {
                            scoreName: 'BIO_WAIST',
                            biometric: { labelKey: 'HEADING_REPORT_WAIST', units: 'in' },
                            planScore: { scoreName: 'BIO_WAIST' }
                        }
                    ],
                },
                BIO_LDL: {
                    headingKey: 'HEADING_REPORT_CHOLESTEROL',
                    meters: [
                        {
                            scoreName: 'BIO_LDL',
                            biometric: { labelKey: 'HEADING_REPORT_LDL', units: 'mg/dl' },
                            planScore: { scoreName: 'BIO_LDL', }
                        },
                        {
                            scoreName: 'BIO_HDL',
                            biometric: { labelKey: 'HEADING_REPORT_HDL', units: 'mg/dl' },
                            planScore: { scoreName: 'BIO_HDL' }
                        }
                    ],
                },
                SCORE_BP: {
                    headingKey: 'HEADING_REPORT_BLOOD_PRESSURE',
                    meters: [
                        {
                            scoreName: 'BIO_BP_SYS',
                            biometric: { labelKey: 'HEADING_REPORT_SYSTOLIC', units: 'mm Hg' },
                        },
                        {
                            scoreName: 'BIO_BP_DIA',
                            biometric: { labelKey: 'HEADING_REPORT_DIASTOLIC', units: 'mm Hg' },
                        }
                    ],
                    summaryPlanScore: { scoreName: 'SCORE_BP', }
                },
                SCORE_METAB: {
                    headingKey: 'HEADING_REPORT_METABOLIC',
                    meters: [
                        {
                            scoreName: 'BIO_TG',
                            biometric: { labelKey: 'HEADING_REPORT_TRIGLYCERIDES', units: 'mg/dl' },
                        },
                        {
                            scoreName: 'BIO_FBG',
                            biometric: { labelKey: 'HEADING_REPORT_BLOOD_GLUCOSE', units: 'mg/dl' },
                        },
                        {
                            scoreName: 'BIO_A1C',
                            biometric: { labelKey: 'HEADING_REPORT_A1C', units: '%', precision: 1 },
                        }
                    ],
                    summaryPlanScore: { scoreName: 'SCORE_METAB' },
                },
                TOTAL_SCORE: {
                    headingKey: 'HEADING_REPORT_CVR_SCORE',
                    meters: [{ scoreName: 'TOTAL_SCORE' }],
                    summaryPlanScore: { scoreName: 'TOTAL_SCORE' },
                },
                FRAMINGHAM_SCORE: {
                    headingKey: 'HEADING_REPORT_FRAMINGHAM_SCORE',
                    summaryPlanScore: { scoreName: 'FRAMINGHAM_SCORE' },
                },
                ACC_AHA_SCORE: {
                    headingKey: 'HEADING_REPORT_ACC_AHA_SCORE',
                    summaryPlanScore: { scoreName: 'ACC_AHA_SCORE' },
                },
                SCORE_PMH: {
                    headingKey: 'HEADING_REPORT_SCORE_PMH',
                    meters: [{
                        scoreName: 'SCORE_PMH',
                        planScore: { scoreName: 'SCORE_PMH' }
                    }],
                },

                OK_PAIN: {
                    headingKey: 'LABEL_OK_PAIN',
                    indicatorLight: { scoreName: 'OK_PAIN', labelKey: 'LABEL_OK_PAIN' }
                },
                OK_ADLS: {
                    headingKey: 'LABEL_OK_ADLS',
                    indicatorLight: { scoreName: 'OK_ADLS', labelKey: 'LABEL_OK_ADLS' }
                },
                OK_ASSESS_FALLS: {
                    headingKey: 'LABEL_OK_FALLS',
                    indicatorLight: { scoreName: 'OK_ASSESS_FALLS', labelKey: 'LABEL_OK_FALLS' }
                },
                SCORE_GAIT_SPEED: {
                    headingKey: 'LABEL_SCORE_GAIT_SPEED',
                    modalIndex: 2,
                    meters: [{
                        scoreName: 'SCORE_GAIT_SPEED',
                        planScore: { scoreName: 'SCORE_GAIT_SPEED' }
                    }]
                },
                SCORE_SIT_TO_STAND: {
                    headingKey: 'HEADING_REPORT_SIT_TO_STAND',
                    meters: [{
                        scoreName: 'SCORE_SIT_TO_STAND',
                        planScore: { scoreName: 'SCORE_SIT_TO_STAND' }
                    }]
                },
                SCORE_STAIRS: {
                    headingKey: 'HEADING_REPORT_STAIRS',
                    meters: [{
                        scoreName: 'SCORE_STAIRS',
                        planScore: { scoreName: 'SCORE_STAIRS' }
                    }]
                },
                SCORE_SIT_REACH: {
                    headingKey: 'HEADING_REPORT_SIT_REACH',
                    meters: [{
                        scoreName: 'SCORE_SIT_REACH',
                        planScore: { scoreName: 'SCORE_SIT_REACH' }
                    }]
                },
                SCORE_CURLS: {
                    headingKey: 'HEADING_REPORT_CURLS',
                    meters: [{
                        scoreName: 'SCORE_CURLS',
                        planScore: { scoreName: 'SCORE_CURLS' }
                    }]
                },

                // Social Factors

                OK_LITERACY: {
                    headingKey: 'LABEL_OK_LITERACY',
                    indicatorLight: { scoreName: 'OK_LITERACY', labelKey: 'LABEL_OK_LITERACY' }
                },
                OK_HEALTH_LITERACY: {
                    headingKey: 'LABEL_OK_HEALTH_LITERACY',
                    indicatorLight: { scoreName: 'OK_HEALTH_LITERACY', labelKey: 'LABEL_OK_HEALTH_LITERACY' }
                },
                OK_SECURITY: {
                    headingKey: 'LABEL_OK_SECURITY',
                    indicatorLight: { scoreName: 'OK_SECURITY', labelKey: 'LABEL_OK_SECURITY' }
                },
                OK_FOOD_SECURITY: {
                    headingKey: 'LABEL_OK_FOOD_SECURITY',
                    indicatorLight: { scoreName: 'OK_FOOD_SECURITY', labelKey: 'LABEL_OK_FOOD_SECURITY' }
                },
                OK_COMMUNITY: {
                    headingKey: 'LABEL_OK_COMMUNITY',
                    indicatorLight: { scoreName: 'OK_COMMUNITY', labelKey: 'LABEL_OK_COMMUNITY' }
                },
                OK_TRANSPORTATION: {
                    headingKey: 'LABEL_OK_TRANSPORTATION',
                    indicatorLight: { scoreName: 'OK_TRANSPORTATION', labelKey: 'LABEL_OK_TRANSPORTATION' }
                },
                OK_FINANCES: {
                    headingKey: 'LABEL_OK_FINANCES',
                    indicatorLight: { scoreName: 'OK_FINANCES', labelKey: 'LABEL_OK_FINANCES' }
                },
                OK_POVERTY: {
                    headingKey: 'LABEL_OK_POVERTY',
                    indicatorLight: { scoreName: 'OK_POVERTY', labelKey: 'LABEL_OK_POVERTY' }
                },
                OK_FEELS_WELL: {
                    headingKey: 'LABEL_OK_FEELS_WELL',
                    indicatorLight: { scoreName: 'OK_FEELS_WELL', labelKey: 'LABEL_OK_FEELS_WELL' }
                },
                OK_HRQOL: {
                    headingKey: 'LABEL_OK_HRQOL',
                    indicatorLight: { scoreName: 'OK_HRQOL', labelKey: 'LABEL_OK_HRQOL' }
                },
                OK_SOCIAL_SUPPORT: {
                    headingKey: 'LABEL_OK_SOCIAL_SUPPORT',
                    indicatorLight: { scoreName: 'OK_SOCIAL_SUPPORT', labelKey: 'LABEL_OK_SOCIAL_SUPPORT' }
                },
                OK_CENTERED: {
                    headingKey: 'LABEL_OK_CENTERED',
                    indicatorLight: { scoreName: 'OK_CENTERED', labelKey: 'LABEL_OK_CENTERED' }
                },

                PHQ9_SCORE: {
                    headingKey: 'HEADING_REPORT_PHQ9',
                    meters: [{
                        scoreName: 'PHQ9_SCORE',
                        planScore: { scoreName: 'PHQ9_SCORE' }
                    }]
                },
                GAD7_SCORE: {
                    headingKey: 'HEADING_REPORT_GAD7',
                    meters: [{
                        scoreName: 'GAD7_SCORE',
                        planScore: { scoreName: 'GAD7_SCORE' }
                    }]
                },
                ORT_SCORE: {
                    headingKey: 'HEADING_REPORT_ORT',
                    meters: [{
                        scoreName: 'ORT_SCORE',
                        planScore: { scoreName: 'ORT_SCORE' }
                    }]
                },
                AUDIT_SCORE: {
                    headingKey: 'HEADING_REPORT_AUDIT',
                    meters: [{
                        scoreName: 'AUDIT_SCORE',
                        planScore: { scoreName: 'AUDIT_SCORE' }
                    }]
                },

            }
        }
    }
}
